class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const min = parseInt(this.input.min);
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', value <= min);
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    // Cache media queries to avoid recreating on each call
    this._mobileMediaQuery = window.matchMedia('(max-width: 990px)');
    this._reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    // Add passive flag to prevent scroll blocking
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this), { passive: false })
    );
    this.querySelectorAll('.btn-facet-filters-form-close').forEach((btnClose) => {
      btnClose.addEventListener(
        'click',
        function () {
          let summary = btnClose.closest('details')?.querySelector('summary');
          summary.click();
        },
        { passive: true }
      );
    });
    this.querySelectorAll('button:not(.localization-selector)').forEach((button) =>
      button.addEventListener('click', this.onCloseButtonClick.bind(this), { passive: true })
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const isOpen = detailsElement.hasAttribute('open');

    // Cache DOM queries to avoid multiple traversals
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const nextElement = summaryElement.nextElementSibling;
    const buttonElement = detailsElement.querySelector('button');

    const addTrapFocus = () => {
      trapFocus(nextElement, buttonElement);
      nextElement.removeEventListener('transitionend', addTrapFocus);
    };

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();

      // Batch ALL operations in single RAF to avoid cascade recalculations
      requestAnimationFrame(() => {
        // Menu drawer operation
        isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

        // Viewport height (only on mobile, use cached media query)
        if (this._mobileMediaQuery.matches) {
          document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        }
      });
    } else {
      // Batch ALL DOM updates in single RAF to prevent multiple style recalculations
      requestAnimationFrame(() => {
        // Read phase - check all conditions first
        const shouldAddTrapFocus = !this._reducedMotionQuery || this._reducedMotionQuery.matches;

        // Write phase - batch all DOM changes
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        if (parentMenuElement) {
          parentMenuElement.classList.add('submenu-open');
        }

        // Defer trapFocus to prevent blocking current frame
        requestAnimationFrame(() => {
          if (shouldAddTrapFocus) {
            addTrapFocus();
          } else {
            nextElement.addEventListener('transitionend', addTrapFocus);
          }
        });
      });
    }
  }

  openMenuDrawer(summaryElement) {
    // Batch classList updates in RAF
    requestAnimationFrame(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    });

    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    // Batch all DOM updates in RAF to avoid multiple layout recalculations
    requestAnimationFrame(() => {
      this.mainDetailsToggle.classList.remove('menu-opening');

      // Cache querySelectorAll results
      const allDetails = this.mainDetailsToggle.querySelectorAll('details');
      const allSubmenus = this.mainDetailsToggle.querySelectorAll('.submenu-open');

      allDetails.forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });

      allSubmenus.forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });

      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    });

    // removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) {
      requestAnimationFrame(() => {
        elementToFocus?.setAttribute('aria-expanded', false);
      });
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    // Cache DOM query
    const parentMenuElement = detailsElement.closest('.submenu-open');
    const summaryElement = detailsElement.querySelector('summary');

    // Batch all DOM updates in RAF
    requestAnimationFrame(() => {
      if (parentMenuElement) {
        parentMenuElement.classList.remove('submenu-open');
      }
      detailsElement.classList.remove('menu-opening');
      summaryElement.setAttribute('aria-expanded', false);
    });

    // removeTrapFocus(summaryElement);
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.closeButton = document.querySelector('.menu-drawer__close');
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (e) => {
        this.closeMenuDrawer(e, this.mainDetailsToggle.querySelector('summary'));
      });
    }
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;

    // Cache getBoundingClientRect() and batch style updates in RAF
    requestAnimationFrame(() => {
      const headerBottom = this.header.getBoundingClientRect().bottom;
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(headerBottom - this.borderOffset)}px`
      );
    });

    // Batch classList operations in RAF
    requestAnimationFrame(() => {
      this.header.classList.add('menu-open');
      this.mainDetailsToggle.classList.add('menu-opening');
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);

    // Batch classList update in RAF
    requestAnimationFrame(() => {
      this.header.classList.remove('menu-open');
    });

    window.removeEventListener('resize', this.onResize);
  }

  onResize = (() => {
    let resizeRaf;
    let cachedHeaderBottom = null;
    let cachedViewportHeight = null;

    return () => {
      // Cancel previous RAF to debounce
      if (resizeRaf) {
        cancelAnimationFrame(resizeRaf);
      }

      resizeRaf = requestAnimationFrame(() => {
        // Cache getBoundingClientRect result
        if (this.header) {
          const headerBottom = this.header.getBoundingClientRect().bottom;
          const headerValue = parseInt(headerBottom - this.borderOffset);

          // Only update if changed
          if (headerValue !== cachedHeaderBottom) {
            document.documentElement.style.setProperty('--header-bottom-position', `${headerValue}px`);
            cachedHeaderBottom = headerValue;
          }
        }

        // Cache viewport height
        const viewportHeight = window.innerHeight;
        if (viewportHeight !== cachedViewportHeight) {
          document.documentElement.style.setProperty('--viewport-height', `${viewportHeight}px`);
          cachedViewportHeight = viewportHeight;
        }
      });
    };
  })();
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) {
          this.hide();
        }
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }

    if (this.querySelector('button[aria-label="Close"]')) {
      this.querySelector('button[aria-label="Close"]').addEventListener('click', () => {
        this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    // removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}

customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}

customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);
